/**
 * API client for announcements (the Home-page announcement bar). Mirrors the
 * backend schemas (Announcement*) and the /announcements router. Managing is
 * system-admin only; fetchActiveAnnouncements is readable by any user.
 */
import { API } from "../config/api"

const TOKEN_KEY = "invi_token"

export interface Announcement {
  id: number
  scope: string // "global" | "dealer_group"
  dealer_group_id: number | null
  message: string
  level: string // "info" | "warning" | "success"
  is_active: boolean
  dismissible: boolean
  starts_at: string | null
  ends_at: string | null
  created_at: string
}

export interface AnnouncementInput {
  scope: string
  dealer_group_id?: number | null
  message: string
  level: string
  is_active: boolean
  dismissible: boolean
  starts_at?: string | null
  ends_at?: string | null
}

const authHeaders = (): HeadersInit => {
  const token = localStorage.getItem(TOKEN_KEY)
  return token
    ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" }
    : { "Content-Type": "application/json" }
}

async function handleResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const data = await res.json().catch(() => null)
    throw new Error(data?.detail || `Request failed (${res.status})`)
  }
  return res.json() as Promise<T>
}

export async function fetchActiveAnnouncements(): Promise<Announcement[]> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.announcements.active}`, {
    headers: authHeaders(),
  })
  return handleResponse<Announcement[]>(res)
}

export async function fetchAnnouncements(): Promise<Announcement[]> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.announcements.base}`, {
    headers: authHeaders(),
  })
  return handleResponse<Announcement[]>(res)
}

export async function createAnnouncement(input: AnnouncementInput): Promise<Announcement> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.announcements.base}`, {
    method: "POST",
    headers: authHeaders(),
    body: JSON.stringify(input),
  })
  return handleResponse<Announcement>(res)
}

export async function updateAnnouncement(
  id: number,
  patch: Partial<AnnouncementInput>,
): Promise<Announcement> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.announcements.byId(id)}`, {
    method: "PATCH",
    headers: authHeaders(),
    body: JSON.stringify(patch),
  })
  return handleResponse<Announcement>(res)
}

export async function deleteAnnouncement(id: number): Promise<void> {
  const res = await fetch(`${API.baseUrl}${API.endpoints.announcements.byId(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  })
  await handleResponse<{ status: string }>(res)
}
